import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteDialog";

export const ChefBand = () => (
  <section className="bg-[#FAF8F5] py-24 sm:py-32" data-testid="chef-band">
    <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="order-2 lg:order-1 lg:col-span-6">
        <p className="eyebrow text-[#9a7614]">05 — Culifect Chef Agency</p>
        <MaskedLines lines={["Chefs wanted.", "Good kitchens", "waiting."]} italic={1} className="mt-4 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-6xl" />
        <p className="mt-6 max-w-lg text-stone-700">
          Head chefs, sous chefs, chefs de partie and cooks: join our agency team and pick up shifts on Culifect projects in care homes, schools and workplaces.
        </p>
        <Link to="/chefs" className="btn-ink mt-8" data-testid="chef-band-apply-link">
          Apply with your CV <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="order-1 lg:order-2 lg:col-span-6">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }}>
          <motion.div
            variants={{ hidden: { clipPath: "inset(0% 100% 0% 0% round 28px)" }, show: { clipPath: "inset(0% 0% 0% 0% round 28px)" } }}
            transition={{ duration: 1.3, ease: [0.76, 0, 0.24, 1] }}
            className="overflow-hidden rounded-[28px]"
          >
            <img src="/images/chef_saucing_lobster.jpg" alt="Chef saucing a plated dish" className="h-[380px] w-full object-cover sm:h-[520px]" />
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

export const ResourcesBand = () => (
  <section className="bg-[#EFEAE1] py-24 sm:py-28" data-testid="resources-band">
    <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <p className="eyebrow text-[#9a7614]">06 — Resources</p>
        <MaskedLines lines={["Guides & recipe", "books to download."]} italic={1} className="mt-4 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-6xl" />
        <p className="mt-6 max-w-md text-stone-700">Kitchen set-up guides with food safety checklists, plus 30-day recipe books by Chef Wilbert Chaniwa. Instant PDF download.</p>
        <Link to="/resources" className="btn-ink mt-8" data-testid="resources-band-link">Browse resources <ArrowUpRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid grid-cols-4 gap-3 sm:gap-5 lg:col-span-7">
        {["Care_Home_Kitchen_Setup_and_Operation_Guide", "School_Kitchen_Setup_and_Operation_Guide", "30_Day_Soft_Foods_Recipe_Book_by_Wilbert_Chaniwa", "30_Day_School_Lunches_Recipe_Book_by_Wilbert_Chaniwa"].map((f, i) => (
          <motion.img
            key={f}
            src={`/images/docs/Culifect_${f}.webp`}
            alt=""
            initial={{ opacity: 0, y: 50, rotate: i % 2 ? 4 : -4 }}
            whileInView={{ opacity: 1, y: i % 2 ? 24 : 0, rotate: i % 2 ? 3 : -3 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -10, rotate: 0 }}
            className="aspect-[1/1.414] w-full rounded-md object-cover shadow-xl ring-1 ring-black/10"
          />
        ))}
      </div>
    </div>
  </section>
);

export const FinalCta = () => {
  const { openQuote } = useQuote();
  return (
    <section className="relative overflow-hidden bg-[#141412] py-28 text-[#FAF8F5] sm:py-40 grain" data-testid="final-cta">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#C59B27]/15 blur-[120px]" />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
        <MaskedLines lines={["Let's feed", "your people."]} italic={1} className="font-display text-5xl font-bold leading-[0.98] tracking-tight sm:text-7xl lg:text-[8rem]" />
        <Reveal delay={0.2} className="mt-12 flex flex-wrap gap-3">
          <button onClick={() => openQuote()} className="btn-gold" data-testid="final-quote-button">
            Request a quote <ArrowUpRight className="h-4 w-4" />
          </button>
          <Link to="/sectors/care-homes" className="btn-ghost text-[#FAF8F5] hover:bg-white/10" data-testid="final-plans-link">
            Browse subscription plans
          </Link>
        </Reveal>
      </div>
    </section>
  );
};
